import React, { useEffect, useRef } from 'react';
import { FormProvider, Path } from 'react-hook-form';
import { Listing, useListing, Column, ListingForm } from 'components/shared/TableListing';
import {
  CriteriaBodyCell,
  CriteriaHeadCell,
  getAvailableConditions,
} from 'modules/jsonRulesCriterias/businessCriteria';
import { TopActionPanel } from './TopActionPanel';
import { BottomActionPanel } from './BottomActionPanel';
import { configurationListingColumns } from './listingConfig';
import { allAftermarketProductsTypes } from '../../../constants';
import { ProductConfig, ProductConfigListingContext } from '../../../types';

interface Props {
  isReadOnly: boolean;
  initialConfigKeys: string[];
  initialConfigurationList: ProductConfig[];
  onSubscribe: (value: ProductConfig[]) => void;
}

export const AftermarketProductConfigurator = ({
  isReadOnly,
  initialConfigKeys,
  initialConfigurationList,
  onSubscribe,
}: Props) => {
  const isInitialColumnsAdded = useRef<boolean>(false);

  const {
    form,
    columns,
    tableData,
    handleAddRow,
    handleRemoveRow,
    handleAddColumn,
    handleRemoveColumn,
    handleUpdateColumnType,
  } = useListing<ProductConfig, ProductConfigListingContext>(
    {
      headerColumns: configurationListingColumns,
      bodyFields: initialConfigurationList ?? [],
    },
    { shouldUnregister: false, onSubscribe, disabled: isReadOnly }
  );

  const handleAddConfigColumn = (type: string) => {
    handleAddColumn(
      {
        header: 'Condition',
        renderKey: type,
        columnType: type,
        renderHeadCell: ({ columnIndex, column, existingColumnsTypes }) => {
          const unregisterBodyFields = () => {
            const bodyFields = form.getValues('bodyFields');

            bodyFields.forEach((_, rowIndex) => {
              const resetPath = `bodyFields.${rowIndex}.criteria.${column.columnType}` as Path<
                ListingForm<ProductConfig>
              >;
              form.unregister(resetPath);
            });
          };

          const onChange = (nextValue: string) => {
            unregisterBodyFields();
            handleUpdateColumnType(columnIndex, nextValue);
          };

          const onRemove = () => {
            unregisterBodyFields();
            handleRemoveColumn(columnIndex);
          };

          return (
            <CriteriaHeadCell
              onChange={onChange}
              onRemove={onRemove}
              isReadOnly={isReadOnly}
              currentValue={column.columnType}
              listOfFacts={allAftermarketProductsTypes}
              addedCriteriaTypes={existingColumnsTypes}
            />
          );
        },
        renderCell: ({ column, rowIndex }) => {
          const name = `bodyFields.${rowIndex}.criteria.${column.columnType}` as Path<ListingForm<ProductConfig>>;
          return <CriteriaBodyCell control={form.control} ruleType={column.columnType} name={name} />;
        },
      } satisfies Column<ProductConfig, ProductConfigListingContext>,
      1
    );
  };

  useEffect(() => {
    if (!isInitialColumnsAdded.current) {
      initialConfigKeys.forEach(handleAddConfigColumn);

      isInitialColumnsAdded.current = true;
    }
  }, [handleAddConfigColumn, initialConfigKeys]);

  const onAddConfigurationColumn = () => {
    const added = columns.map(({ columnType }) => columnType).filter(Boolean);
    const availableCriteriaType = getAvailableConditions(added as string[]);

    if (!availableCriteriaType) return;
    handleAddConfigColumn(availableCriteriaType);
  };

  const handleCopyLastConfig = () => {
    const rows = form.getValues('bodyFields');
    handleAddRow(rows[rows.length - 1]);
  };

  return (
    <FormProvider {...form}>
      <Listing<ProductConfig, ProductConfigListingContext>
        columns={columns}
        tableData={tableData}
        injectContext={{
          form,
          isReadOnly,
          handleAddRow,
          handleRemoveRow,
          handleRemoveColumn,
          handleCopyLastConfig,
          onAddConfigurationColumn,
        }}
        ActionPanelTop={TopActionPanel}
        ActionPanelBottom={BottomActionPanel}
      />
    </FormProvider>
  );
};
